import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Button, Grid, FormControl, FormControlLabel, Checkbox, FormHelperText } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { GridTextField } from "components";
import { getProfileContactData, getSettings, patchSettings, putProfileContactData } from "api/user";
import { getAliasAccountsContactData, putAliasAccountsContactData, getAliasAccountsSettings, patchAliasAccountsSettings } from "api/alias";
import i18n from "./i18n";

// prop types
interface EditContactDataDialogProps {
    aliasUserId?: string,
    show: boolean,
    onClose: () => void,
}

// edit contact data dialog
const EditContactDataDialog = ({ aliasUserId, show, onClose }: EditContactDataDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [allow3rdPartyCheckIns, setAllow3rdPartyCheckIns] = useState(false);
    const [city, setCity] = useState("");
    const [fullName, setFullName] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [postalCode, setPostalCode] = useState("");
    const [street, setStreet] = useState("");
    const [telephoneCountryCode, setTelephoneCountryCode] = useState("+49");
    const [telephoneNumber, setTelephoneNumber] = useState("");

    // fetch contact data
    const fetchContactData = async () => {
        const contactData = aliasUserId ? await getAliasAccountsContactData({ aliasUserId }) : await getProfileContactData();
        const settings = aliasUserId ? await getAliasAccountsSettings({ aliasUserId }) : await getSettings();
        setAllow3rdPartyCheckIns(settings.allow3rdPartyCheckIns);
        setCity(contactData.city);
        setFullName(contactData.fullName);
        setPostalCode(contactData.postalCode);
        setStreet(contactData.street);
        setTelephoneCountryCode(contactData.telephoneCountryCode);
        setTelephoneNumber(contactData.telephoneNumber);
    };
    useEffect(() => { fetchContactData(); }, [aliasUserId]);

    // save action
    const saveAction = async () => {
        setLoading(true);
        try {
            const contactData = { city, fullName, postalCode, street, telephoneCountryCode, telephoneNumber };
            if (aliasUserId) {
                await putAliasAccountsContactData({ ...contactData, aliasUserId });
                await patchAliasAccountsSettings({ allow3rdPartyCheckIns, aliasUserId });
            } else {
                await putProfileContactData(contactData);
                await patchSettings({ allow3rdPartyCheckIns });
            }
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
            onClose();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <Dialog fullWidth open={show} onClose={onClose}>
            <DialogTitle>{i18n.editContactData}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <GridTextField size={6} label={i18n.fullName} value={fullName} onChange={setFullName} />
                    <GridTextField size={6} label={i18n.street} value={street} onChange={setStreet} />
                    <GridTextField size={6} label={i18n.postalCode} value={postalCode} onChange={setPostalCode} />
                    <GridTextField size={6} label={i18n.city} value={city} onChange={setCity} />
                    <GridTextField size={6} label={i18n.telephoneCountryCode} value={telephoneCountryCode} onChange={setTelephoneCountryCode} />
                    <GridTextField size={6} label={i18n.telephoneNumber} value={telephoneNumber} onChange={setTelephoneNumber} />
                    <Grid item xs={12}>
                        <FormControl>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={allow3rdPartyCheckIns}
                                        color="primary"
                                        onChange={(e) => setAllow3rdPartyCheckIns(e.target.checked)}
                                    />
                                )}
                                label={i18n.allow3rdPartyCheckIns}
                            />
                            {allow3rdPartyCheckIns && !(fullName && street && postalCode && city && telephoneCountryCode && telephoneNumber) && (
                                <FormHelperText>{i18n.notice3rdPartyContactDataRequired}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" disabled={isLoading} onClick={saveAction}>
                    {i18n.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditContactDataDialog;
